<template>
  <scoped>
    <DeliverableToolbar
      v-if="deliverableEnumerationResponse"
      v-model:value="deliverableInput"
      :deliverableOrders="deliverableEnumerationResponse.orders"
      :deliverableTypes="deliverableEnumerationResponse.types"
      @update="update()"
      @reset="reset()"
    />
    <DeliverableList
      v-if="deliverableResponse"
      v-model:selectedIndex="selectedIndex"
      :items="deliverableResponse.records"
      @select="select()"
    />
    <Paginator
      v-if="deliverableResponse"
      v-model:currentPage="deliverableInput.plot.page"
      :itemTotal="deliverableResponse.recordsTotal"
      :pageItems="deliverableInput.plot.items"
      @update="update()"
    />
    <ErrorModal
      v-if="error"
      v-model:error="error"
    />
  </scoped>
</template>

<script setup lang="ts">
  import DeliverableList from "@/components/lists/deliverable.vue"
  import DeliverableToolbar from "@/components/toolbars/deliverable.vue"
  import {nextTick, onBeforeMount, PropType, ref} from "vue"
  import {
    Deliverable,
    DeliverableEnumerationResponse,
    DeliverableInput,
    DeliverableResponse
  } from "@/endpoints/rutsolve/gql/generated/graphql"
  import Paginator from "@/components/paginator.vue"
  import {Deliverable as DeliverableClient} from "@/endpoints/rutsolve/gql/resolvers/deliverable"
  import {Enumeration as EnumerationClient} from "@/endpoints/rutsolve/gql/resolvers/enumeration"
  import {DeliverableSelector as Storage} from "@/storage/deliverable_selector"
  import ErrorModal from "@/components/modals/error.vue"

  const value = defineModel("value", {
    default: null,
    required: false,
    type: Object as PropType<Deliverable>
  })

  const emits = defineEmits([
    "select",
  ])

  const error = ref<Error>(null)
  const deliverableClient = new DeliverableClient()
  const enumerationClient = new EnumerationClient()
  const deliverableResponse = ref<DeliverableResponse>(null)
  const deliverableEnumerationResponse = ref<DeliverableEnumerationResponse>(null)
  const deliverableInput = ref<DeliverableInput>(null)
  const selectedIndex = ref<number>(null)

  onBeforeMount( () => {
    read()
  })

  function update() {
    Storage.writeDeliverableInput(deliverableInput.value)
    read()
  }

  async function select() {
    const selectedDeliverable = deliverableResponse.value.records[selectedIndex.value]
    value.value = selectedDeliverable
    Storage.writeSelectedDeliverableID(selectedDeliverable.id)
    await nextTick()
    emits("select")
  }

  function reset() {
    Storage.deleteDeliverableInput()
    Storage.deleteSelectedDeliverableID()
    selectedIndex.value = null
    value.value = null
    read()
  }

  async function read() {
    try {
      deliverableEnumerationResponse.value = await enumerationClient.readDeliverableEnumerations()
      const storedDeliverableInput = Storage.readDeliverableInput()
      deliverableInput.value = structuredClone(storedDeliverableInput)
      deliverableResponse.value = await deliverableClient.selector(storedDeliverableInput)
      if (!deliverableResponse.value || !deliverableResponse.value.records) {
        return
      }
      const storedSelectedDeliverableID = Storage.readSelectedDeliverableID()
      for (const [index, deliverable] of deliverableResponse.value.records.entries()) {
        if (deliverable.id == storedSelectedDeliverableID) {
          selectedIndex.value = index
          value.value = deliverable
          break
        }
      }
    } catch(e) {
      error.value = e
    }
  }
</script>
